import React, { useState } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'

interface ManualQRInputProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (qrData: string) => void
  isLoading?: boolean
}

const ManualQRInput: React.FC<ManualQRInputProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const { colors } = useTheme()
  const { deviceType } = useDeviceDetection()
  const [qrData, setQrData] = useState('')
  const isMobileDevice = deviceType === 'mobile'

  const handleSubmit = () => {
    const trimmedData = qrData.trim()

    if (!trimmedData) {
      toastError('Ingresa el ID de la reserva')
      return
    }

    onSubmit(trimmedData)
    setQrData('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      {/* Modal de entrada manual - Fullscreen en móvil */}
      <div
        className="bg-white overflow-y-auto flex flex-col rounded-lg shadow-2xl"
        style={{
          width: isMobileDevice ? '100%' : '100%',
          maxWidth: isMobileDevice ? '100%' : '384px', // max-w-sm = 384px
          height: isMobileDevice ? '100vh' : 'auto',
          maxHeight: isMobileDevice ? '100vh' : '90vh',
          padding: isMobileDevice ? '12px' : '24px',
          borderRadius: isMobileDevice ? '0px' : '8px',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2
            className="font-bold text-gray-900"
            style={{
              fontSize: isMobileDevice ? '20px' : '24px',
            }}
          >
            ✏️ Entrada Manual
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
            style={{
              fontSize: isMobileDevice ? '20px' : '24px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Instrucciones */}
        <div
          className="bg-blue-50 border border-blue-200 rounded-lg mb-4 text-blue-900"
          style={{
            padding: isMobileDevice ? '12px' : '16px',
            fontSize: isMobileDevice ? '12px' : '14px',
          }}
        >
          💡 Ingresa el ID de la reserva del huésped.
        </div>

        {/* Input */}
        <div className="flex-1 mb-4 flex flex-col">
          <label
            className="font-medium text-gray-700 mb-2"
            style={{
              fontSize: isMobileDevice ? '13px' : '14px',
            }}
          >
            ID de Reserva
          </label>
          <input
            type="text"
            value={qrData}
            onChange={(e) => setQrData(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ej: a1b2c3d4-e5f6-7890-abcd-ef1234567890"
            disabled={isLoading}
            className="px-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
            style={{
              fontSize: isMobileDevice ? '14px' : '14px',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}20`
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.boxShadow = 'none'
            }}
          />
        </div>

        {/* Botones */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-lg font-medium transition-colors text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              padding: isMobileDevice ? '12px' : '12px',
              fontSize: isMobileDevice ? '14px' : '14px',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !qrData.trim()}
            className="flex-1 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              padding: isMobileDevice ? '12px' : '12px',
              fontSize: isMobileDevice ? '14px' : '14px',
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              if (!isLoading && qrData.trim()) {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            {isLoading ? '⏳ Validando...' : '✓ Validar'}
          </button>
        </div>

        {/* Nota de ayuda */}
        <div
          className="pt-4 border-t border-gray-200"
          style={{
            fontSize: isMobileDevice ? '11px' : '12px',
          }}
        >
          <p className="text-gray-600 font-medium mb-2">¿Dónde encontrar el ID de reserva?</p>
          <ul className="text-gray-600 space-y-1 list-disc list-inside">
            <li>En el email de confirmación (número de reserva)</li>
            <li>En la página de confirmación del sitio web</li>
            <li>En el PDF adjunto (en la tabla superior)</li>
            <li>En el sistema de administración del camping</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default ManualQRInput
